import React from 'react';
import {
  MdDirectionsBike,
  MdDirectionsRun,
  MdDownhillSkiing,
  MdEmojiEvents,
  MdFlight,
  MdGroups,
  MdIceSkating,
  MdMusicNote,
  MdPool,
  MdSailing,
  MdSportsBaseball,
  MdSportsBasketball,
  MdSportsCricket,
  MdSportsFootball,
  MdSportsGolf,
  MdSportsHandball,
  MdSportsHockey,
  MdSportsKabaddi,
  MdSportsMma,
  MdSportsMotorsports,
  MdSportsRugby,
  MdSportsScore,
  MdSportsSoccer,
  MdSportsTennis,
  MdSportsVolleyball,
  MdTwoWheeler,
} from 'react-icons/md';
import { FaBullseye, FaHorse } from 'react-icons/fa';

/**
 * Demo-only background images, copied from the provider's public CDN and
 * committed under public/assets/images/event-types/.
 *
 * These are placeholders. The intended end state is: images in S3, urls stored
 * per event type in the DB and fetched, so nothing image-related ships in the
 * bundle. When that lands, only getEventTypeImage() needs to change.
 */
const EVENT_TYPE_IMAGE_DIR = '/assets/images/event-types';

/**
 * Used for the 20 provider event types that have no artwork at all (Boxing,
 * Ski, Volleyball, Sumo, Padel, Fighting, Winter Games, ...). The banner swaps
 * to this on image load error rather than consulting a hardcoded id list, so
 * dropping a new `{id}.webp` into the folder starts working with no code change.
 */
export const EVENT_TYPE_FALLBACK_IMAGE = `${EVENT_TYPE_IMAGE_DIR}/default.webp`;

export const getEventTypeImage = (eventTypeId?: number): string =>
  eventTypeId
    ? `${EVENT_TYPE_IMAGE_DIR}/${eventTypeId}.webp`
    : EVENT_TYPE_FALLBACK_IMAGE;

/**
 * Provider event-type id -> icon.
 *
 * Keyed on id, never on name: eventType.name arrives localized from the API, so
 * it is not a stable key. Ids are the sportsevents365 event-type ids.
 * Types not listed here fall through to the isTeamSport/concert heuristic below,
 * which mirrors the existing behaviour in CustomAutoCompleteEvents.
 */
const EVENT_TYPE_ICONS: Record<number, React.ComponentType<any>> = {
  1000: MdSportsSoccer, // Football (Soccer)
  1001: MdSportsTennis, // Tennis
  1002: MdSportsBasketball, // Basketball
  1003: MdSportsMotorsports, // Car Racing
  1005: MdSportsBaseball, // Baseball
  1006: MdSportsFootball, // US Football
  1007: MdTwoWheeler, // Motorbike Racing
  1008: MdDirectionsBike, // Bicycle Racing
  1009: MdDirectionsRun, // Athletics
  1010: MdSportsHockey, // Ice Hockey
  1011: MdPool, // Swimming
  1012: MdSportsHandball, // Handball
  1014: MdSportsMma, // Boxing
  1016: MdSailing, // Sailing
  1017: MdSportsScore, // Multiple Sports
  1019: MdSportsCricket, // Cricket
  1020: MdSportsRugby, // Rugby
  1021: MdDownhillSkiing, // Ski
  1023: MdMusicNote, // Entertainment (concerts & shows)
  1024: MdSportsVolleyball, // Volleyball
  1025: MdSportsTennis, // Table Tennis
  1026: MdSportsKabaddi, // Wrestling
  1028: MdSportsGolf, // Golf
  1029: MdPool, // Water Polo
  1030: MdSportsMotorsports, // Motor Sport
  1032: MdSportsKabaddi, // Sumo
  1034: MdSportsBasketball, // Netball
  1043: MdSportsSoccer, // Futsal
  1046: MdIceSkating, // Figure Skating
  1047: MdSportsHockey, // Field hockey
  1048: MdSportsMma, // Bellator MMA
  1049: MdFlight, // Air Racing
  1050: MdSportsTennis, // Badminton
  1051: FaHorse, // Horse Racing
  1052: MdSportsScore, // Snooker
  1053: FaHorse, // Equestrian Sports
  1057: MdSportsTennis, // Padel
  1058: MdPool, // Aquatic sports
  1059: FaBullseye, // Darts
  1060: MdSportsMma, // Fighting
  1061: MdDownhillSkiing, // Winter Games
};

interface EventTypeLike {
  id?: number;
  isTeamSport?: boolean;
  concert?: boolean;
}

export const getEventTypeIcon = (
  eventType?: EventTypeLike
): React.ReactNode => {
  const MappedIcon = eventType?.id
    ? EVENT_TYPE_ICONS[eventType.id]
    : undefined;
  if (MappedIcon) {
    return <MappedIcon />;
  }
  if (eventType?.concert) {
    return <MdMusicNote />;
  }
  if (eventType?.isTeamSport) {
    return <MdGroups />;
  }
  return <MdEmojiEvents />;
};
